/**
 * PhotoThumbnailGrid — responsive grid of thumbnails for PhotoAttachment.
 *
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.2 + §2.9 (legacy union types).
 *
 * Contract:
 *  - `photos` accepts a union: `(PhotoRef | string)[]`.
 *      - `PhotoRef` → look up thumb via `usePhotoStore.getThumb(photoId)`.
 *      - `string`   → legacy base64 data-URL; render directly.
 *  - Empty state renders NOTHING — the parent shell decides messaging.
 *  - Skeleton placeholder while the initial thumb pass hydrates.
 *  - `onOpen(index)` / `onDelete(index)` bubble user actions up to the shell.
 *  - `caption={true}` shows a small inline TITLE INPUT below each PhotoRef
 *    thumb (editable) OR a read-only text label in `disabled` mode. Legacy
 *    base64 string entries have no caption slot — nothing renders for them.
 *    FT-350 (2026-07-09): title input surfaced inline for discoverability.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, ImageOff } from 'lucide-react';

const gridClasses = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3';

/**
 * @param {Object} props
 * @param {(import('./types').PhotoRef | string)[]} props.photos
 * @param {import('./hooks/usePhotoStore').PhotoStore} props.store  Only .getThumb() is used.
 * @param {(index: number) => void} [props.onOpen]
 * @param {(index: number) => void} [props.onDelete]
 * @param {(photoId: string, nextCaption: string) => void} [props.onCaptionChange]
 * @param {boolean} [props.caption]
 * @param {boolean} [props.disabled]
 * @param {string} [props.testId]
 */
export function PhotoThumbnailGrid({
  photos,
  store,
  onOpen,
  onDelete,
  onCaptionChange,
  caption = false,
  disabled = false,
  testId = 'photo-thumbnail-grid',
}) {
  if (!Array.isArray(photos) || photos.length === 0) {
    // Parent controls empty-state messaging.
    return null;
  }

  return (
    <div className={gridClasses} data-testid={testId}>
      {photos.map((photo, index) => (
        <PhotoThumbnailCell
          key={cellKey(photo, index)}
          photo={photo}
          index={index}
          store={store}
          onOpen={onOpen}
          onDelete={onDelete}
          onCaptionChange={onCaptionChange}
          caption={caption}
          disabled={disabled}
          testId={`${testId}-cell-${index}`}
        />
      ))}
    </div>
  );
}

function cellKey(photo, index) {
  if (typeof photo === 'string') return `legacy:${index}`;
  return photo?.photoId ? `ref:${photo.photoId}` : `idx:${index}`;
}

/**
 * Single cell — encapsulates the thumb-fetch effect so grid re-renders don't
 * re-trigger every fetch. Each cell mounts once per photoId.
 */
function PhotoThumbnailCell({
  photo,
  index,
  store,
  onOpen,
  onDelete,
  onCaptionChange,
  caption,
  disabled,
  testId,
}) {
  const isLegacy = typeof photo === 'string';
  const [thumbUrl, setThumbUrl] = useState(isLegacy ? photo : null);
  const [status, setStatus] = useState(isLegacy ? 'ready' : 'loading');
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (isLegacy) return undefined; // no async fetch needed
    if (!store?.getThumb || !photo?.photoId) {
      setStatus('missing');
      return undefined;
    }
    cancelledRef.current = false;
    setStatus('loading');
    let mounted = true;
    (async () => {
      try {
        const result = await store.getThumb(photo.photoId);
        if (!mounted || cancelledRef.current) return;
        if (result?.url) {
          setThumbUrl(result.url);
          setStatus('ready');
        } else {
          setStatus('missing');
        }
      } catch {
        if (!mounted) return;
        setStatus('missing');
      }
    })();
    return () => {
      mounted = false;
      cancelledRef.current = true;
    };
    // NOTE: store.getThumb identity is stable per usePhotoStore memo — safe
    // to include as a dep without triggering re-fetch storms.
  }, [store, photo, isLegacy]);

  const captionText = !isLegacy && caption ? photo?.caption || '' : '';

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="relative w-full aspect-square rounded overflow-hidden bg-gray-100 border border-gray-200">
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid={`${testId}-skeleton`}>
            <Loader2 className="h-6 w-6 text-gray-400 animate-spin" aria-hidden />
          </div>
        )}
        {status === 'missing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400" data-testid={`${testId}-missing`}>
            <ImageOff className="h-6 w-6" aria-hidden />
            <span className="mt-1 text-[10px]">Not synced</span>
          </div>
        )}
        {status === 'ready' && thumbUrl && (
          <button
            type="button"
            onClick={() => onOpen?.(index)}
            className="absolute inset-0 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            aria-label={`Open photo ${index + 1}`}
            data-testid={`${testId}-open`}
          >
            <img
              src={thumbUrl}
              alt={captionText || `Photo ${index + 1}`}
              className="w-full h-full object-cover"
              draggable={false}
            />
          </button>
        )}
        {!disabled && onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(index); }}
            aria-label={`Delete photo ${index + 1}`}
            data-testid={`${testId}-delete`}
            className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center rounded-full bg-black bg-opacity-60 text-white hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {caption && !isLegacy && !disabled && (
        <input
          type="text"
          value={captionText}
          onChange={(e) => onCaptionChange?.(photo?.photoId, e.target.value)}
          placeholder="Add title..."
          maxLength={80}
          className="mt-1 w-full text-xs bg-transparent border-b border-gray-200 focus:border-cyan-500 focus:outline-none px-1 py-0.5 text-gray-700"
          data-testid={`${testId}-caption-input`}
          aria-label={`Title for photo ${index + 1}`}
        />
      )}
      {caption && !isLegacy && disabled && (
        <p
          className="text-xs text-gray-500 line-clamp-2 min-h-[1rem]"
          data-testid={`${testId}-caption`}
          title={captionText}
        >
          {captionText || ' '}
        </p>
      )}
    </div>
  );
}

export default PhotoThumbnailGrid;
